import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppDispatch, useAppSelector } from '@/store';
import { selectActiveTimers, stopTimerAsync } from '@/store/slices/timerSlice';
import { taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { Task, TaskStatus } from '@/types';
import { TaskContextType } from './types';
import { validateTaskCompletion } from './utils';

export interface UseTaskTableActionsProps {
  context: TaskContextType;
  projectId?: string;
  allTasks?: Task[];
}

export interface UseTaskTableActionsReturn {
  // Mutation handlers
  handleCheckboxChange: (task: Task) => void;
  handleDelete: (task: Task) => void;
  handleComplete: (task: Task) => Promise<void>;
  confirmDelete: (task: Task) => Promise<void>;
  
  // Confirmation states
  confirmCompleteTask: Task | null;
  confirmDeleteTask: Task | null;
  blockingWarning: string | null;
  blockingTask: Task | null;
  
  // State setters
  setConfirmCompleteTask: (task: Task | null) => void;
  setConfirmDeleteTask: (task: Task | null) => void;
  clearBlockingWarning: () => void;
  
  // Mutation states
  isCompleting: boolean;
  isDeleting: boolean;
}

/**
 * Hook for task table actions (complete, delete, status toggle)
 * Handles mutations, validation, and query invalidation
 */
export const useTaskTableActions = ({
  context,
  projectId,
  allTasks = [],
}: UseTaskTableActionsProps): UseTaskTableActionsReturn => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const activeTimers = useAppSelector(selectActiveTimers);

  // Confirmation modal states
  const [confirmCompleteTask, setConfirmCompleteTask] = useState<Task | null>(null);
  const [confirmDeleteTask, setConfirmDeleteTask] = useState<Task | null>(null);
  const [blockingWarning, setBlockingWarning] = useState<string | null>(null);
  const [blockingTask, setBlockingTask] = useState<Task | null>(null);

  /**
   * Invalidate all relevant task queries based on context
   */
  const invalidateTaskQueries = () => {
    // Always invalidate these
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myTasks });
    queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todaysTasks });

    // Invalidate project-specific queries if in project context
    if (context === 'projectTasks' && projectId) {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.projectTasks(projectId) });
    }

    // Use predicate-based invalidation for comprehensive coverage
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0];
        return key === 'tasks' || (Array.isArray(query.queryKey) && query.queryKey.includes('project'));
      },
    });
  };

  /**
   * Complete task mutation
   */
  const markCompleteMutation = useMutation({
    mutationFn: async (taskId: string) => {
      // Stop timer if active
      const activeTimer = activeTimers.find(t => t.taskId === taskId);
      if (activeTimer) {
        await dispatch(stopTimerAsync({ taskId, reason: 'Task completed' })).unwrap();
      }
      
      return taskService.completeTask(taskId);
    },
    onSuccess: () => {
      invalidateTaskQueries();
      setConfirmCompleteTask(null);
    },
    onError: (error) => {
      console.error('Failed to complete task:', error);
      setConfirmCompleteTask(null);
    },
  });

  /**
   * Delete task mutation
   */
  const deleteTaskMutation = useMutation({
    mutationFn: (taskId: string) => taskService.deleteTask(taskId),
    onSuccess: () => {
      invalidateTaskQueries();
      setConfirmDeleteTask(null);
    },
    onError: (error) => {
      console.error('Failed to delete task:', error);
      setConfirmDeleteTask(null);
    },
  });

  /**
   * Handle checkbox change (status toggle)
   */
  const handleCheckboxChange = (task: Task) => {
    if (task.status === TaskStatus.COMPLETED) {
      // Reopen task - no validation needed
      taskService.updateTaskStatus(task.id, TaskStatus.IN_PROGRESS)
        .then(() => invalidateTaskQueries())
        .catch(error => console.error('Failed to update task status:', error));
    } else {
      // Validate completion
      const validation = validateTaskCompletion(task, allTasks);
      
      if (!validation.canComplete) {
        // Show blocking warning
        setBlockingWarning(validation.reason || 'Cannot complete task');
        setBlockingTask(validation.blockingTasks?.[0] || null);
        return;
      }

      // Show confirmation modal
      setConfirmCompleteTask(task);
    }
  };

  /**
   * Handle delete action
   */
  const handleDelete = (task: Task) => {
    setConfirmDeleteTask(task);
  };

  /**
   * Handle complete action (from confirmation)
   */
  const handleComplete = async (task: Task) => {
    await markCompleteMutation.mutateAsync(task.id);
  };

  /**
   * Confirm and execute delete action
   */
  const confirmDelete = async (task: Task) => {
    await deleteTaskMutation.mutateAsync(task.id);
  };

  /**
   * Clear blocking warning
   */
  const clearBlockingWarning = () => {
    setBlockingWarning(null);
    setBlockingTask(null);
  };

  return {
    // Handlers
    handleCheckboxChange,
    handleDelete,
    handleComplete,
    confirmDelete,

    // Confirmation states
    confirmCompleteTask,
    confirmDeleteTask,
    blockingWarning,
    blockingTask,

    // State setters
    setConfirmCompleteTask,
    setConfirmDeleteTask,
    clearBlockingWarning,

    // Mutation states
    isCompleting: markCompleteMutation.isPending,
    isDeleting: deleteTaskMutation.isPending,
  };
};
